import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@ui-nuvertos/shared';

export interface JournalVoucherFilters {
  limit?: number;
  offset?: number;
  transaction_no?: string;
  posting_date_from?: string;
  posting_date_to?: string;
  status?: string;
  is_deleted?: boolean | number;
  get_references?: string;
  include_children?: boolean;
}

export interface JournalVoucherApiResponse {
  data: {
    column_preference?: {
      id: string;
      columns_list: any[];
    };
    journal_vouchers: any[];
    total: number;
    limit: number;
    offset: number;
  };
  message?: string;
  status?: string;
}

export interface JournalVoucherSingleResponse {
  data: any;
  message?: string;
  status?: string;
}

@Injectable({
  providedIn: 'root'
})
export class JournalVoucherService {
  private readonly API_BASE_URL = environment.accountingUrl;

  constructor(private http: HttpClient) {}

  /**
   * Get all journal vouchers with optional filters
   * Endpoint: GET /api/v1/journal-vouchers
   * Query Parameters: limit, offset, transaction_no, posting_date, status, is_deleted
   */
  getJournalVouchers(filters?: JournalVoucherFilters): Observable<JournalVoucherApiResponse> {
    let params = new HttpParams();

    if (filters) {
      // Add pagination parameters
      if (filters.limit) {
        params = params.set('limit', filters.limit.toString());
      }
      if (filters.offset !== undefined) {
        params = params.set('offset', filters.offset.toString());
      }

      // Add filter parameters
      if (filters.transaction_no) {
        params = params.set('transaction_no', filters.transaction_no);
      }
      if (filters.posting_date_from) {
      params = params.set('posting_date_from', filters.posting_date_from);
      }

      if (filters.posting_date_to) {
       params = params.set('posting_date_to', filters.posting_date_to);
      }

      if (filters.status) {
        params = params.set('status', filters.status);
      }
      if (filters.is_deleted !== undefined) {
        params = params.set('is_deleted', typeof filters.is_deleted === 'boolean' ? (filters.is_deleted ? '1' : '0') : filters.is_deleted.toString());
      }
      if (filters.get_references) {
        params = params.set('get_references', filters.get_references);
      }
      if (filters.include_children) {
        params = params.set('include_children', filters.include_children);
      }
    }

    return this.http.get<JournalVoucherApiResponse>(`${this.API_BASE_URL}/api/v1/journal-vouchers/`, {
      params,
      withCredentials: true
    });
  }

  /**
   * Get single journal voucher by ID
   * Endpoint: GET /api/v1/journal-vouchers/{id}
   */
  getJournalVoucherById(id: string | number): Observable<JournalVoucherSingleResponse> {
    return this.http.get<JournalVoucherSingleResponse>(`${this.API_BASE_URL}/api/v1/journal-vouchers/${id}`, {
      withCredentials: true
    });
  }

  /**
   * Create a new journal voucher
   * Endpoint: POST /api/v1/journal-vouchers
   */
  createJournalVoucher(journalVoucherData: any): Observable<any> {
    return this.http.post<any>(`${this.API_BASE_URL}/api/v1/journal-vouchers/`, journalVoucherData, {
      withCredentials: true
    });
  }

  /**
   * Update an existing journal voucher
   * Endpoint: PUT /api/v1/journal-vouchers/{id}
   */
  updateJournalVoucher(id: string | number, journalVoucherData: any): Observable<any> {
    return this.http.put<any>(`${this.API_BASE_URL}/api/v1/journal-vouchers/${id}`, journalVoucherData, {
      withCredentials: true
    });
  }

  /**
   * Delete a journal voucher (soft delete)
   * Endpoint: DELETE /api/v1/journal-vouchers/{id}
   */
  deleteJournalVoucher(id: string | number): Observable<any> {
    return this.http.delete<any>(`${this.API_BASE_URL}/api/v1/journal-vouchers/${id}`, {
      withCredentials: true
    });
  }

  /**
   * Post a draft journal voucher
   * Endpoint: POST /api/v1/journal-vouchers/{id}/post
   */
  postJournalVoucher(id: string | number): Observable<any> {
    return this.http.post<any>(`${this.API_BASE_URL}/api/v1/journal-vouchers/${id}/post`, {}, {
      withCredentials: true
    });
  }

  /**
   * Void a posted journal voucher
   * Endpoint: POST /api/v1/journal-vouchers/{id}/void
   */
  voidJournalVoucher(id: string | number, reason: string): Observable<any> {
    return this.http.post<any>(`${this.API_BASE_URL}/api/v1/journal-vouchers/${id}/void`, { reason }, {
      withCredentials: true
    });
  }

  /**
   * Get all form templates
   * Endpoint: GET /api/v1/journal-voucher-form-templates/
   */
  getFormTemplates(): Observable<any> {
    const url = `${this.API_BASE_URL}/api/v1/journal-voucher-form-templates/`;
    return this.http.get<any>(url, { withCredentials: true });
  }

  /**
   * Get default form template
   * Endpoint: GET /api/v1/journal-voucher-form-templates/default
   */
  getDefaultFormTemplate(): Observable<any> {
    const url = `${this.API_BASE_URL}/api/v1/journal-voucher-form-templates/default`;
    return this.http.get<any>(url, { withCredentials: true });
  }

  /**
   * Get form template by ID
   * Endpoint: GET /api/v1/journal-voucher-form-templates/{id}
   */
  getFormTemplateById(templateId: string | number): Observable<any> {
    const url = `${this.API_BASE_URL}/api/v1/journal-voucher-form-templates/${templateId}`;
    return this.http.get<any>(url, { withCredentials: true });
  }

  /**
   * Get form template by ID with custom fields
   * Endpoint: GET /api/v1/journal-voucher-form-templates/{id}/with-custom-fields
   */
  getFormTemplateByIdWithCustomFields(templateId: string | number): Observable<any> {
    const url = `${this.API_BASE_URL}/api/v1/journal-voucher-form-templates/${templateId}/with-custom-fields`;
    return this.http.get<any>(url, { withCredentials: true });
  }

  /**
   * Create client invoice mappings for a JV line
   * Endpoint: POST /api/v1/journal-vouchers/{id}/lines/{line_id}/client-invoices
   */
  createJVLineClientInvoiceMappings(jvId: string | number, lineId: string | number, mappings: any): Observable<any> {
    const url = `${this.API_BASE_URL}/api/v1/journal-vouchers/${jvId}/lines/${lineId}/client-invoices`;
    return this.http.post<any>(url, mappings, { withCredentials: true });
  }

  /**
   * Update client invoice mappings for a JV line
   * Endpoint: PUT /api/v1/journal-vouchers/{id}/lines/{line_id}/client-invoices
   */
  updateJVLineClientInvoiceMappings(jvId: string | number, lineId: string | number, mappings: any): Observable<any> {
    const url = `${this.API_BASE_URL}/api/v1/journal-vouchers/${jvId}/lines/${lineId}/client-invoices`;
    return this.http.put<any>(url, mappings, { withCredentials: true });
  }

  /**
   * Create vendor invoice mappings for a JV line
   * Endpoint: POST /api/v1/journal-vouchers/{id}/lines/{line_id}/vendor-invoices
   */
  createJVLineVendorInvoiceMappings(jvId: string | number, lineId: string | number, mappings: any): Observable<any> {
    const url = `${this.API_BASE_URL}/api/v1/journal-vouchers/${jvId}/lines/${lineId}/vendor-invoices`;
    return this.http.post<any>(url, mappings, { withCredentials: true });
  }

  /**
   * Update vendor invoice mappings for a JV line
   * Endpoint: PUT /api/v1/journal-vouchers/{id}/lines/{line_id}/vendor-invoices
   */
  updateJVLineVendorInvoiceMappings(jvId: string | number, lineId: string | number, mappings: any): Observable<any> {
    const url = `${this.API_BASE_URL}/api/v1/journal-vouchers/${jvId}/lines/${lineId}/vendor-invoices`;
    return this.http.put<any>(url, mappings, { withCredentials: true });
  }

  /**
   * Get journal voucher line item by ID
   * Endpoint: GET /api/v1/journal-vouchers/{id}/lines/{line_id}
   */
  getJournalVoucherLineById(jvId: string | number, lineId: string | number): Observable<any> {
    const url = `${this.API_BASE_URL}/api/v1/journal-vouchers/${jvId}/lines/${lineId}`;
    return this.http.get<any>(url, { withCredentials: true });
  }

  /**
   * Get audit logs for a journal voucher
   * Endpoint: GET /api/v1/journal-vouchers/:id/audit-logs
   */
  getAuditLogs(journalVoucherId: string | number, filterParams?: any): Observable<any> {
    let params = new HttpParams();

    if (filterParams) {
      if (filterParams.limit) {
        params = params.set('limit', filterParams.limit.toString());
      }
      if (filterParams.offset !== undefined) {
        params = params.set('offset', filterParams.offset.toString());
      }
      if (filterParams.date) {
        params = params.set('date', filterParams.date);
      }
      if (filterParams.user_id) {
        params = params.set('user_id', filterParams.user_id);
      }
    }

    const url = `${this.API_BASE_URL}/api/v1/journal-vouchers/${journalVoucherId}/audit-logs`;
    return this.http.get<any>(url, {
      params,
      withCredentials: true
    });
  }

  /**
   * Get GL Impact data for a journal voucher
   * Endpoint: GET /api/v1/gl-impact
   */
  getGlImpact(documentType: string, documentId: string, includeVoidEntries?: boolean): Observable<any> {
    const url = `${this.API_BASE_URL}/api/v1/gl-impact`;
    let params = new HttpParams()
      .set('document_type', documentType)
      .set('document_id', String(documentId));
    
    if (includeVoidEntries !== undefined) {
      params = params.set('include_void_entries', includeVoidEntries.toString());
    }
    
    return this.http.get<any>(url, { params, withCredentials: true });
  }

  /**
   * Get print data for a journal voucher
   * Endpoint: GET /api/v1/journal-vouchers/{id}/print-data
   */
  getPrintData(id: string | number): Observable<any> {
    const url = `${this.API_BASE_URL}/api/v1/journal-vouchers/${id}/print-data`;
    return this.http.get<any>(url, { withCredentials: true });
  }
}

